import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { Location } from "@angular/common";
import { UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";

import { IcEnvironment } from "@intocare-medapp/environment";

import { MustEnableMfaError } from "app/shared/errors/must-enable-mfa.error";
import { MFARequiredError, PasswordPolicy } from "@intocare-medapp/aws/http/entities";
import { SimpleToastService } from "../../shared/modules";
import { AuthModel } from "../../shared";
import { PermissionDeniedError } from "../../shared/errors/permission-denied.error";
import { IcOrganisation } from "@intocare-medapp/organisation/ic-organisation";
import { BehaviorSubject, Subscription } from "rxjs";
import { hasLowerCase, hasMinLength, hasNumbers, hasSymbols, hasUpperCase } from "@intocare-medapp/validators";
import { catchError, distinctUntilChanged, finalize, tap } from "rxjs/operators";

@Component({
	templateUrl: "./confirm-password.component.html",
	styleUrls: ["./confirm-password.component.scss"],
	standalone: false,
})
export class ConfirmPasswordComponent implements OnInit, OnDestroy {
	private readonly location = inject(Location);
	private readonly authModel = inject(AuthModel);
	private readonly toastService = inject(SimpleToastService);
	private readonly translateService = inject(TranslateService);
	private readonly router = inject(Router);
	private readonly icOrganisation = inject(IcOrganisation);
	private readonly icEnvironment = inject(IcEnvironment);

	appVersion = this.icEnvironment.getEnvironment().APP_VERSION;

	passwordPolicy: PasswordPolicy = {
		MinimumLength: 8,
		RequireLowercase: true,
		RequireUppercase: true,
		RequireNumbers: true,
		RequireSymbols: true,
	};
	validationRules = [];
	validationChecksState: { key: string; active: boolean; value: undefined }[] = this.validationRules.map((key) => ({
		key,
		active: false,
		value: undefined,
	}));
	// @ts-expect-error TS2564
	formSubscription: Subscription;
	// @ts-expect-error TS2564
	passwordPolicySubscription: Subscription;
	loadingPasswordPolicy$ = new BehaviorSubject<boolean>(false);

	form = new UntypedFormGroup(
		{
			newPassword: new UntypedFormControl("", {
				validators: [Validators.required],
			}),
			verifyPassword: new UntypedFormControl("", {
				validators: [Validators.required],
			}),
		},
		// @ts-expect-error TS2345
		{
			validators: [
				(form: UntypedFormGroup) => {
					// @ts-expect-error TS2531
					return form.get("newPassword").value === form.get("verifyPassword").value
						? null
						: { passwordsDoNoMatch: true };
				},
			],
		},
	);

	invalidLength(controlName: string) {
		const control = this.form.get(controlName);

		// @ts-expect-error TS18047
		return control.touched && control.hasError("minlength");
	}

	controlRequired(controlName: string) {
		const control = this.form.get(controlName);

		// @ts-expect-error TS18047
		return control.touched && control.hasError("required");
	}

	confirm(form: { newPassword: string; verifyPassword: string }) {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		this.authModel.confirmPassword(form.newPassword).subscribe({
			next: () => {
				this.router.navigate(["user-briefing"]);
			},
			error: (err) => {
				this.authModel.hideLoadingDialog();

				if (err instanceof MustEnableMfaError) {
					return this.router.navigate(["auth/mfa-activation"]);
				}

				if (err instanceof MFARequiredError) {
					return this.router.navigate(["auth/mfa-entry"]);
				}

				let message = "auth.errors.confirm_password";

				if (err instanceof PermissionDeniedError) {
					message = "auth.errors.permission_denied";
				}

				if (err.code === "NetworkError") {
					message = "general.warnings.network_lost";
				}

				this.cancel();
				this.toastService.error(this.translateService.instant(message));
			},
		});
	}

	cancel() {
		this.location.back();
	}

	validatePassword(password: string): [string, boolean][] {
		const errors: ValidationErrors = {};

		const validationSet = new Map<string, boolean>([
			["MinimumLength", hasMinLength(password, { minlength: this.passwordPolicy.MinimumLength })],
			["RequireLowercase", hasLowerCase(password)],
			["RequireUppercase", hasUpperCase(password)],
			["RequireNumbers", hasNumbers(password)],
			["RequireSymbols", hasSymbols(password)],
		]);

		// @ts-expect-error TS7034
		const validationResponse = [];

		// @ts-expect-error TS2345
		validationSet.forEach((value, key) => this.validationRules.includes(key) && validationResponse.push([key, value]));

		// @ts-expect-error TS7005
		validationResponse.forEach(([key, value]) => {
			errors[key] = !value;
		});

		// @ts-expect-error TS7005
		return validationResponse;
	}

	hasPasswordPolicyError() {
		return this.validationChecksState.find(({ active }) => !active);
	}

	ngOnInit(): void {
		const organisation = this.icOrganisation.getOrganisation();

		this.form.reset();

		this.loadingPasswordPolicy$.next(true);
		this.passwordPolicySubscription = this.authModel
			.getUserpoolData(organisation)
			.pipe(
				tap((userpool: any) => {
					this.passwordPolicy = userpool.passwordPolicy;
					// @ts-expect-error TS2322
					this.validationRules = this.passwordPolicy
						? Object.entries(this.passwordPolicy)
								.map(([key, value]) => (value ? key : null))
								.filter(Boolean)
						: [];
					this.validationChecksState = this.validatePassword(this.form?.value?.credentials?.password ?? "").map(
						([key, value]) => ({
							key,
							active: value,
							// @ts-expect-error TS7053
							value: this.passwordPolicy[key],
						}),
					);
				}),
				catchError(() => {
					this.toastService.error(
						this.translateService.instant("user-settings.modules.password-policy.no-password-policy-found"),
					);
					this.router.navigate(["select-organisation"]);
					return [];
				}),
				finalize(() => this.loadingPasswordPolicy$.next(false)),
			)
			.subscribe();

		this.formSubscription = this.form.valueChanges
			.pipe(
				distinctUntilChanged(),
				tap(({ newPassword }: { newPassword?: string; verifyPassword?: string }) => {
					this.form.markAllAsTouched();

					this.validationChecksState = this.validatePassword(newPassword || "").map(([key, value]) => ({
						key,
						active: value,
						// @ts-expect-error TS7053
						value: this.passwordPolicy[key] || null,
					}));
				}),
			)
			.subscribe();
	}

	ngOnDestroy(): void {
		this.formSubscription.unsubscribe();
		this.passwordPolicySubscription.unsubscribe();
	}
}
